import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

type Product = {
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  rating: number;
  tags?: string[];
  group_id: string | null;
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/products/${id}`, {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        timeout: 10000,
      });
      setProduct(res.data);
      await fetchTags();
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : '请求失败';
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`/api/products/${id}/tags`, {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        timeout: 5000,
      });
      const tagsData = Array.isArray(res.data) ? res.data.map((t: any) => t.name) : [];
      setTags(tagsData);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setTags([]);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-2/3 h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="w-full md:w-1/3 h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Alert variant="destructive" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error || 'Product not found'}</AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => window.history.back()}>
            返回产品列表
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{product.title}</h1>
        </div>
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-2/3">
            <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md h-96">
              <img
                src={product.image || '/placeholder.svg'}
                alt={product.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
            </div>
          </div>
          <div className="w-full md:w-1/3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Product Details</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {product.description || 'No description available'}
              </p>
              <div className="mb-4">
                <span className="font-medium text-gray-900 dark:text-white">Rating: </span>
                <span className="text-yellow-500">{product.rating}</span>
              </div>
              <div className="mb-4">
                <span className="font-medium text-gray-900 dark:text-white">Tags: </span>
                {tags && tags.length > 0 ? (
                  tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs mr-2">
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">No tags</span>
                )}
              </div>
              <Button
                variant="default"
                className="w-full mt-4"
                onClick={() => {
                  if (product.site_url) {
                    window.open(product.site_url, '_blank');
                  } else {
                    toast({ title: '错误', description: '没有设置网站地址', variant: 'destructive' });
                  }
                }}
              >
                访问网站
              </Button>
              <Button variant="outline" className="w-full mt-2" onClick={() => window.history.back()}>
                返回产品列表
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;